export const FLEET_VIEW_CHANNELS_STORAGE_KEY = 'fleetView.detailBlockSettings.v2'

export const DETAIL_BLOCKS = [
  {
    key: 'engine',
    title: 'Engine',
    defaults: [
      { key: 'engineRPM', label: 'Engine RPM', unit: '', decimals: 0 },
      { key: 'ecmHours', label: 'ECM hours', unit: '', decimals: 1 },
      { key: 'engineIdleHours', label: 'Engine idle hours', unit: '', decimals: 1 },
      { key: 'engineITR', label: 'Engine ITR', unit: '', decimals: 2 },
      { key: 'coolantTemp', label: 'Coolant temp', unit: '°F', decimals: 1 },
      { key: 'fuelRate', label: 'Fuel rate', unit: 'gph', decimals: 1 },
    ],
  },
  {
    key: 'transmission',
    title: 'Transmission',
    defaults: [
      { key: 'pressure', label: 'Pressure', unit: 'psi', decimals: 1 },
      { key: 'temperature', label: 'Temperature', unit: '°F', decimals: 1 },
      { key: 'torqueConvTemp', label: 'Torque conv temp', unit: '°F', decimals: 1 },
      { key: 'inputSpeed', label: 'Input speed', unit: 'rpm', decimals: 0 },
      { key: 'outputSpeed', label: 'Output speed', unit: 'rpm', decimals: 0 },
      { key: 'hrsRunning', label: 'Hrs running', unit: '', decimals: 1 },
      { key: 'hrsInGear', label: 'Hrs in gear', unit: '', decimals: 1 },
      { key: 'currentGear', label: 'Current gear', unit: '', decimals: 0 },
    ],
  },
  {
    key: 'pump',
    title: 'Pump (power / fluid end)',
    defaults: [
      { key: 'dischargePressure', label: 'Discharge pressure', unit: 'psi', decimals: 0 },
      { key: 'lubeOilPressure', label: 'Lube oil pressure', unit: 'psi', decimals: 1 },
      { key: 'lubeOilTemp', label: 'Lube oil temp', unit: '°F', decimals: 1 },
      { key: 'pumpSpeed', label: 'Pump speed', unit: 'rpm', decimals: 0 },
      { key: 'workhoursPowerEnd', label: 'Workhours power end', unit: '', decimals: 1 },
      { key: 'workhoursFluidEnd', label: 'Workhours fluid end', unit: '', decimals: 1 },
      { key: 'rodLoad', label: 'Rod load', unit: '', decimals: 0 },
    ],
  },
  {
    key: 'hydraulic',
    title: 'Hydraulic system',
    defaults: [
      { key: 'fanSpeed', label: 'Fan speed', unit: 'rpm', decimals: 0 },
      { key: 'cPumpSpeed', label: 'C-Pump speed', unit: 'rpm', decimals: 0 },
      { key: 'chargePressure', label: 'Charge pressure', unit: 'psi', decimals: 1 },
      { key: 'workPressure', label: 'Work pressure', unit: 'psi', decimals: 0 },
      { key: 'setpoint', label: 'Setpoint', unit: '', decimals: 0 },
      { key: 'temperature', label: 'Temperature', unit: '°F', decimals: 1 },
    ],
  },
  {
    key: 'dgb',
    title: 'DGB — Dynamic Gas Blending',
    defaults: [
      { key: 'actualRate', label: 'Actual rate', unit: '', decimals: 1 },
      { key: 'rateSetpoint', label: 'Rate setpoint', unit: '', decimals: 1 },
      { key: 'engineTorque', label: 'Engine torque', unit: '', decimals: 1 },
      { key: 'state', label: 'State', unit: '', decimals: 0, raw: true },
    ],
  },
]

export const DETAIL_BLOCK_KEYS = DETAIL_BLOCKS.map((b) => b.key)

const withIds = (channels, blockKey) => (channels || []).map((ch, index) => ({
  ...ch,
  id: ch.id || `${blockKey}-${ch.key || 'ch'}-${index}`,
  isDefault: ch.isDefault !== false && Boolean(ch.isDefault || ch._default),
  decimals: ch.decimals != null ? ch.decimals : 1,
  unit: ch.unit || '',
  raw: Boolean(ch.raw),
}))

const defaultsForBlock = (blockKey) => {
  const block = DETAIL_BLOCKS.find((b) => b.key === blockKey)
  if (!block) return []
  return withIds(
    block.defaults.map((ch) => ({ ...ch, isDefault: true })),
    blockKey
  )
}

export const emptyDetailBlockSettings = () => (
  DETAIL_BLOCKS.reduce((acc, block) => {
    acc[block.key] = defaultsForBlock(block.key)
    return acc
  }, {})
)

const normalizeBlockChannels = (blockKey, channels) => {
  if (!Array.isArray(channels) || !channels.length) {
    return defaultsForBlock(blockKey)
  }
  return withIds(channels.map((ch) => ({
    ...ch,
    isDefault: Boolean(ch.isDefault),
  })), blockKey)
}

export const loadFleetViewChannelSettings = () => {
  try {
    const raw = localStorage.getItem(FLEET_VIEW_CHANNELS_STORAGE_KEY)
    if (!raw) return emptyDetailBlockSettings()
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') return emptyDetailBlockSettings()

    // Ignore legacy tile-channel settings (Pump/Blender keys)
    if (parsed.Pump || parsed.Blender) {
      return emptyDetailBlockSettings()
    }

    const source = parsed.blocks && typeof parsed.blocks === 'object'
      ? parsed.blocks
      : parsed

    return DETAIL_BLOCKS.reduce((acc, block) => {
      acc[block.key] = normalizeBlockChannels(block.key, source[block.key])
      return acc
    }, {})
  } catch (e) {
    return emptyDetailBlockSettings()
  }
}

export const saveFleetViewChannelSettings = (settings) => {
  localStorage.setItem(
    FLEET_VIEW_CHANNELS_STORAGE_KEY,
    JSON.stringify({ version: 2, blocks: settings || emptyDetailBlockSettings() })
  )
}

export const getDetailBlockMeta = (blockKey) => (
  DETAIL_BLOCKS.find((b) => b.key === blockKey) || DETAIL_BLOCKS[0]
)

export const getDetailBlockChannels = (blockKey) => {
  const settings = loadFleetViewChannelSettings()
  return normalizeBlockChannels(blockKey, settings[blockKey])
}

export const getAllDetailBlockConfigs = () => {
  const settings = loadFleetViewChannelSettings()
  return DETAIL_BLOCKS.map((block) => ({
    ...block,
    fields: normalizeBlockChannels(block.key, settings[block.key]).map((ch) => ({
      key: ch.key,
      label: ch.label,
      decimals: ch.decimals,
      suffix: ch.unit ? ` ${ch.unit}` : '',
      raw: ch.raw,
    })),
  }))
}

export const resetDetailBlockToDefaults = (blockKey) => defaultsForBlock(blockKey)
